import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useRouter } from "next/router";
import { AppRuleNames } from "@Front/Api/Entities/rule";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";
import useUser from "@Front/Hooks/useUser";
import { useOfficeScopedPermissionCan } from "@Front/Hooks/useOfficeScopedPermissionCan";
import { useRolePermissionContext, useRolePermissionsLoading, useRolePermissionsRefresh } from "@Front/Stores/RolePermissionsStore";
import {
	deriveNotaryEmailModalOpenState,
	resolveEffectiveNotaryContactEmailForGate,
	shouldEvaluateNotaryMissingEmailPrompt,
} from "./notaryMissingContactEmailGateHelpers";
import {
	clearNotaryContactEmailPromptDismissedInSession,
	isNotaryContactEmailPromptDismissedInSession,
} from "./notaryMissingContactEmailSession";
import { useNotaryMissingContactEmailForm } from "./useNotaryMissingContactEmailForm";

function useNotaryMissingEmailOpenSync(
	shouldConsider: boolean,
	userUid: string | undefined,
	effectiveContactEmail: string | null | undefined,
): readonly [boolean, Dispatch<SetStateAction<boolean>>] {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		const dismissed = isNotaryContactEmailPromptDismissedInSession();
		const d = deriveNotaryEmailModalOpenState({
			shouldConsider,
			userUid,
			contactEmail: effectiveContactEmail ?? null,
			dismissedInSession: dismissed,
		});
		setOpen(d.open);
		if (d.clearDismissKey) {
			clearNotaryContactEmailPromptDismissedInSession();
		}
	}, [shouldConsider, userUid, effectiveContactEmail]);

	return [open, setOpen] as const;
}

export function useNotaryMissingContactEmailGate(): {
	open: boolean;
	emailInput: string;
	setEmailInput: (v: string) => void;
	error: string | null;
	saving: boolean;
	handleLater: () => void;
	handleSave: () => Promise<void>;
	msg: typeof COMMON_UI_I18N.folderMessages;
} {
	const router = useRouter();
	const { user, refetchUser } = useUser();
	const permissionContext = useRolePermissionContext();
	const permissionsLoading = useRolePermissionsLoading();
	const refreshPermissions = useRolePermissionsRefresh();
	const officeScopedCan = useOfficeScopedPermissionCan();
	const hasOfficeFoldersRead = officeScopedCan(AppRuleNames.officeFolders, "read");
	const shouldConsider = shouldEvaluateNotaryMissingEmailPrompt({
		permissionsLoading,
		permissionContext,
		pathname: router.pathname,
		hasOfficeFoldersRead,
	});
	const effectiveContactEmail = resolveEffectiveNotaryContactEmailForGate({
		userContactEmail: user?.contact?.email,
		permissionContext,
	});
	const [open, setOpen] = useNotaryMissingEmailOpenSync(shouldConsider, user?.uid, effectiveContactEmail);
	const form = useNotaryMissingContactEmailForm(setOpen, refetchUser, refreshPermissions);

	return {
		open,
		...form,
		msg: COMMON_UI_I18N.folderMessages,
	};
}
